import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Input, Spinner, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { ArrowLeft, Users, Check, ScanLine, Trash2, AlertCircle } from "lucide-react";

const CAT_LABELS = { cx1: "CX1", cx2: "CX2", cx3: "CX3", cx3g: "CX3G", cx4: "CX4" };

export default function TerceirosRetiradaNew() {
  const nav = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [availablePallets, setAvailablePallets] = useState([]); // pallets do cliente ainda no estoque
  const [scanned, setScanned] = useState([]);                    // pallets bipados nesta retirada
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState(null);                          // {type, text}
  const [form, setForm] = useState({
    cliente_dono: "",
    data_retirada: new Date().toISOString().slice(0, 10),
    vendedor: "",
    motorista: "",
    placa: "",
    observacoes: "",
  });
  const inputRef = useRef(null);

  // Carrega clientes disponíveis
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/terceiros/pallets");
        setClientes((data.resumo || []).map((r) => ({ nome: r.cliente_dono, sacos: r.sacos, pallets: r.pallets })));
      } catch (e) { setErr(formatErr(e)); }
    })();
  }, []);

  // Ao selecionar cliente, carrega pallets disponíveis (pra montar lista opcional visual)
  useEffect(() => {
    if (!form.cliente_dono) { setAvailablePallets([]); setScanned([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/terceiros/retiradas/available", { params: { cliente_dono: form.cliente_dono } });
        setAvailablePallets(data || []);
        setScanned([]);
      } catch (e) { setErr(formatErr(e)); }
    })();
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [form.cliente_dono]);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3500);
  };

  // Bipagem QR / número
  const scan = async (e) => {
    e.preventDefault();
    const val = q.trim();
    if (!val) return;
    if (!form.cliente_dono) { showMsg("err", "Selecione o cliente antes de bipar."); return; }
    setBusy(true);
    try {
      const { data: pl } = await api.get(`/pallets/resolve`, { params: { q: val, for_terceiros: true } });
      // valida cliente
      if (pl.cliente_dono !== form.cliente_dono) {
        showMsg("err", `Pallet ${pl.numero} pertence a "${pl.cliente_dono}" e não ao cliente selecionado.`);
        return;
      }
      if (scanned.some((s) => s.id === pl.id)) {
        showMsg("warn", `${pl.numero} já foi bipado nesta retirada.`);
        return;
      }
      const restante = Number(pl.restante ?? pl.total_caixas ?? 0);
      // Bipagem: assume que leva TUDO por default; usuário pode ajustar depois
      setScanned((prev) => [{ ...pl, quantidade_ret: restante, restante_original: restante }, ...prev]);
      showMsg("ok", `${pl.numero} adicionado (${fmtNum(restante, 0)} sc disponíveis).`);
    } catch (e2) {
      showMsg("err", formatErr(e2));
    } finally {
      setQ("");
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const removeScan = (pid) => setScanned((prev) => prev.filter((s) => s.id !== pid));

  const setQuantidade = (pid, qtd) => {
    setScanned((prev) => prev.map((p) => {
      if (p.id !== pid) return p;
      const max = Number(p.restante_original || p.total_caixas || 0);
      const n = Math.max(0, Math.min(max, Number(qtd) || 0));
      return { ...p, quantidade_ret: n };
    }));
  };

  // "Bipar todos" — atalho para clientes com poucos pallets
  const scanAll = () => {
    setScanned((prev) => {
      const already = new Set(prev.map((p) => p.id));
      const missing = availablePallets
        .filter((p) => !already.has(p.id))
        .map((p) => {
          const total = Number(p.total_caixas || 0);
          const restante = Math.max(0, total - Number(p.quantidade_expedida || 0));
          return { ...p, quantidade_ret: restante, restante_original: restante };
        })
        .filter((p) => p.quantidade_ret > 0);
      return [...missing, ...prev];
    });
    showMsg("ok", `Todos os pallets disponíveis marcados.`);
  };

  const totals = scanned.reduce((a, p) => {
    a.pallets += 1;
    const q = Number(p.quantidade_ret || 0);
    a.sacos += q;
    const it = (p.itens || [])[0] || {};
    a.por_cat[it.categoria] = (a.por_cat[it.categoria] || 0) + q;
    return a;
  }, { pallets: 0, sacos: 0, por_cat: {} });

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      if (scanned.length === 0) throw new Error("Bipar pelo menos 1 pallet antes de confirmar.");
      const items = scanned
        .filter((p) => Number(p.quantidade_ret || 0) > 0)
        .map((p) => ({ pallet_id: p.id, quantidade: Number(p.quantidade_ret) }));
      if (items.length === 0) throw new Error("Ao menos 1 pallet precisa ter quantidade > 0.");
      await api.post("/terceiros/retiradas", { ...form, items });
      nav("/terceiros/retiradas");
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  return (
    <Layout
      title="Nova Retirada de Terceiros"
      subtitle="Selecione o cliente e BIPE os QR Codes dos pallets a serem entregues"
      actions={<Button variant="secondary" onClick={() => nav("/terceiros/retiradas")}><ArrowLeft size={16} /> Voltar</Button>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* CLIENTE */}
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">1. Cliente</h3>
            {clientes.length === 0 ? (
              <div className="text-sm text-ink-500 p-6 text-center bg-stone-50 rounded-md">
                Nenhum cliente com pallets disponíveis para retirada.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {clientes.map((c) => (
                  <button
                    key={c.nome}
                    onClick={() => setForm({ ...form, cliente_dono: c.nome })}
                    className={`text-left p-3 rounded-lg border transition-colors ${form.cliente_dono === c.nome ? "bg-purple-100 border-purple-500" : "bg-white border-stone-300 hover:bg-purple-50"}`}
                    data-testid={`cliente-opt-${c.nome}`}
                  >
                    <div className="flex items-center gap-2">
                      <Users size={14} className="text-purple-700" />
                      <div className="font-bold text-purple-900">{c.nome}</div>
                    </div>
                    <div className="text-xs text-ink-600 mt-1">
                      {c.pallets} pallets • <span className="font-mono">{fmtNum(c.sacos, 0)} sacos</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* BIPAGEM */}
          {form.cliente_dono && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg font-bold text-ink-900">2. Bipar QR Codes</h3>
                <div className="text-xs text-ink-500">
                  Disponíveis: <span className="font-mono font-bold text-purple-800">{availablePallets.length}</span>
                  {" • "}Bipados: <span className="font-mono font-bold text-emerald-700">{scanned.length}</span>
                </div>
              </div>

              {/* Input de scan */}
              <form onSubmit={scan} className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <ScanLine size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-500" />
                  <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Aponte o leitor de QR Code ou digite o nº da etiqueta (ex.: 3312 · P-3312) e pressione Enter"
                    autoFocus
                    className="w-full rounded-md bg-white border-2 border-purple-300 pl-10 pr-3 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    data-testid="scan-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy || !q.trim()}
                  className="!bg-purple-600 hover:!bg-purple-700"
                  data-testid="scan-submit"
                >
                  {busy ? <Spinner size={14} /> : <><ScanLine size={16} /> Bipar</>}
                </Button>
                {availablePallets.length > 0 && availablePallets.length <= 20 && (
                  <Button variant="secondary" type="button" onClick={scanAll} data-testid="scan-all">
                    Bipar todos
                  </Button>
                )}
              </form>

              {/* Msg de retorno */}
              {msg && (
                <div className={`mb-3 px-3 py-2 rounded-md text-sm flex items-start gap-2 ${
                  msg.type === "ok" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" :
                  msg.type === "warn" ? "bg-amber-50 text-amber-800 border border-amber-200" :
                  "bg-red-50 text-danger border border-red-200"
                }`} data-testid="scan-msg">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{msg.text}</span>
                </div>
              )}

              {/* Lista de pallets bipados */}
              {scanned.length === 0 ? (
                <div className="p-6 text-center text-ink-400 bg-stone-50 rounded-md border-2 border-dashed border-stone-200">
                  <ScanLine size={28} className="mx-auto text-ink-300 mb-2" />
                  <div className="text-sm">Nenhum pallet bipado ainda.</div>
                  <div className="text-xs mt-1">Aponte o leitor para o QR Code do pallet ou digite o número (P-XXXX).</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold mb-1.5">Pallets bipados</div>
                  <table className="w-full text-sm">
                    <thead className="border-b border-ink-200">
                      <tr className="text-xs font-bold uppercase tracking-wider text-ink-500">
                        <th className="px-2 py-2 text-left">Pallet</th>
                        <th className="px-2 py-2 text-left">NF</th>
                        <th className="px-2 py-2 text-left">Categoria</th>
                        <th className="px-2 py-2 text-right">Disp.</th>
                        <th className="px-2 py-2 text-right">Retirar</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {scanned.map((p) => {
                        const it = (p.itens || [])[0] || {};
                        const disp = Number(p.restante_original || p.total_caixas || 0);
                        const qRet = Number(p.quantidade_ret || 0);
                        const parcial = qRet > 0 && qRet < disp;
                        return (
                          <tr key={p.id} className={`border-b border-ink-200 ${parcial ? "bg-amber-50" : "bg-purple-50/40"}`} data-testid={`scanned-${p.id}`}>
                            <td className="px-2 py-2 font-mono font-bold text-purple-800">{p.numero}</td>
                            <td className="px-2 py-2 font-mono">#{p.nota_numero || "—"}</td>
                            <td className="px-2 py-2">
                              <Badge tone="neutral" className="!bg-purple-100 !text-purple-700 !border-purple-200">{CAT_LABELS[it.categoria] || it.categoria}</Badge>
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-xs text-ink-500">{fmtNum(disp, 0)}</td>
                            <td className="px-2 py-2 text-right">
                              <input
                                type="number"
                                min={0}
                                max={disp}
                                value={qRet}
                                onChange={(e) => setQuantidade(p.id, e.target.value)}
                                onFocus={(e) => e.target.select()}
                                className={`w-20 text-right font-mono font-bold border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${parcial ? "border-amber-400 bg-white" : "border-stone-300 bg-white"}`}
                                data-testid={`qtd-${p.id}`}
                              />
                              {parcial && (
                                <div className="text-[9px] text-amber-700 mt-0.5">parcial — resta {fmtNum(disp - qRet, 0)}</div>
                              )}
                            </td>
                            <td className="px-2 py-2 text-right">
                              <button onClick={() => removeScan(p.id)} className="p-1 rounded hover:bg-red-50 text-danger" data-testid={`remove-${p.id}`}>
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {/* TRANSPORTE */}
          {form.cliente_dono && (
            <Card className="p-6">
              <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">3. Transporte</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input label="Data da retirada" type="date" value={form.data_retirada} onChange={(e) => setForm({ ...form, data_retirada: e.target.value })} data-testid="ret-data" />
                <Input label="Vendedor" placeholder="Nome do vendedor" value={form.vendedor} onChange={(e) => setForm({ ...form, vendedor: e.target.value })} data-testid="ret-vendedor" />
                <Input label="Motorista" value={form.motorista} onChange={(e) => setForm({ ...form, motorista: e.target.value })} data-testid="ret-motorista" />
                <Input label="Placa" value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value })} data-testid="ret-placa" />
              </div>
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Observações</div>
                <textarea
                  value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={2}
                  className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  data-testid="ret-obs"
                />
              </div>
            </Card>
          )}
        </div>

        {/* RESUMO LATERAL */}
        <div className="space-y-4">
          <Card className="p-6 sticky top-20">
            <h3 className="font-heading font-bold text-ink-900 mb-2">Resumo</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-ink-500">Cliente</span><span className="font-semibold truncate max-w-[60%] text-right">{form.cliente_dono || "—"}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Pallets bipados</span><span className="font-mono font-bold">{totals.pallets}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Total sacos</span><span className="font-mono font-bold text-purple-800">{fmtNum(totals.sacos, 0)}</span></div>
              <div className="border-t border-ink-200 pt-2 mt-2 space-y-1">
                {Object.entries(totals.por_cat).filter(([, v]) => v > 0).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-xs">
                    <span className="text-ink-500">{CAT_LABELS[k]}</span>
                    <span className="font-mono">{fmtNum(v, 0)} sc</span>
                  </div>
                ))}
              </div>
            </div>
            {err && <div className="text-sm text-danger mt-4 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
            <Button
              onClick={submit}
              disabled={busy || totals.pallets === 0 || !form.cliente_dono}
              size="lg"
              className="w-full mt-5 !bg-purple-600 hover:!bg-purple-700"
              data-testid="submit-retirada"
            >
              {busy ? <Spinner size={16} /> : <><Check size={16} /> Confirmar Retirada</>}
            </Button>
            <div className="text-[10px] text-ink-400 text-center mt-2">
              A retirada só será confirmada após você clicar em &quot;Confirmar Retirada&quot;.
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
